// The matrix block: a strip picking how its sub-questions are charted, the chart itself, and -- for
// the two views that average them -- those averages as a list. Every sub-question is drawn as a
// question of its own below it.

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import Items from '@aytm/app/stats/page/question-views/mx/components/items/Items'
import Chart from '@aytm/app/stats/page/question-views/mx/components/chart/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import ItemsControls from '@aytm/app/stats/page/question-views/components/controls/Controls'
import { BlockContent as OeBlock } from '@aytm/app/stats/page/question-views/oe/Block'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'

import Controls from '@aytm/app/stats/page/question-views/mx/components/Controls'

import { MATRIX_BLOCK_PROP_TYPES } from '@aytm/app/stats/page/question-views/mx/lib/prop-types'
import { calcBoxTableProps, calcMeanTableProps } from '@aytm/app/stats/page/question-views/mx/lib/table-utils'
import useMatrixBlock from '@aytm/app/stats/page/question-views/mx/states/useMatrixBlock'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// a matrix averages its sub-questions in two of its four views, and lists them only there
const TABLE_BUILDERS = { meanScore: calcMeanTableProps, boxScore: calcBoxTableProps }

const Block = props => {
  const { answer, components } = props

  const surfaces = useMatrixBlock(props)

  const table = TABLE_BUILDERS[surfaces.viewMode]?.(surfaces.tableInputs) || null

  // the sub-question list closes the block, so the response count sits under it rather than under
  // the summary; a block drawing no list carries the count itself
  const itemsOn = isComponentOn(components, 'subQuestions')

  // The reference link and the line closing the block -- the response count, or the footer standing
  // in for it -- belong to whichever half draws last. A block whose summary is off hands them to the
  // list rather than opening an empty section above it just to hold them.
  const closingProps = surfaces.summaryOn ? { answer, footer: props.footer } : props

  return (
    <>
      {surfaces.summaryOn ? (
        <BlockWrapper {...props}>
          <ItemsControls {...surfaces.itemsControlsProps} />

          {surfaces.isWCType && surfaces.isWordCloud ? (
            <OeBlock {...surfaces.cloudProps} />
          ) : (
            <>
              {isComponentOn(components, 'controls') && surfaces.chartOn ? (
                <Controls
                  format={props.format}
                  batches={props.batches}
                  viewMode={surfaces.viewMode}
                  maxAnswerPosition={props.maxAnswerPosition}
                  topsMode={surfaces.topsMode}
                  itemsCollapsed={!surfaces.uncollapsed}
                  onToggleBatches={surfaces.onToggleBatches}
                  onModelChange={surfaces.onModelChange}
                />
              ) : null}

              {isComponentOn(components, 'chart') && surfaces.chartOn ? (
                <ChartPlaceholder isLoading={'loading' === answer}>
                  <Chart {...surfaces.chartProps} />
                </ChartPlaceholder>
              ) : null}

              {table && isComponentOn(components, 'table') ? <Table {...table} /> : null}
            </>
          )}
        </BlockWrapper>
      ) : null}

      {itemsOn || !surfaces.summaryOn ? (
        <BlockWrapper {...closingProps}>
          {itemsOn ? <Items {...surfaces.batchesProps} onToggle={surfaces.onBatchToggle} /> : null}
        </BlockWrapper>
      ) : null}
    </>
  )
}

Block.displayName = 'MatrixBlock'

Block.propTypes = MATRIX_BLOCK_PROP_TYPES

export default Block
